import { useEffect } from "react";
import type { UseQueryExecute, UseQueryState } from "urql";

export type DocumentType = "PLAINTEXT" | "PDF" | "WORD" | "POWERPOINT" | "MARKDOWN" | "WEBPAGE" | "AUDIO";

export function documentTypeLabel(docuemntType: DocumentType): string {
    switch (docuemntType) {
        case "PLAINTEXT":
            return "Plain text";
        case "PDF":
            return "PDF file";
        case "WORD":
            return "Word file";
        case "POWERPOINT":
            return "PowerPoint file";
        case "MARKDOWN":
            return "Markdown file";
        case "WEBPAGE":
            return "Webpage";
        case "AUDIO":
            return "Audio file";
        default:
            return "Unknown";
    }
}

export type ProcessingStatus = "PROCESSING" | "FINISHED" | "FAILED";

export function processingStatusLabel(processingStatus: ProcessingStatus): string {
    switch (processingStatus) {
        case "PROCESSING":
            return "Processing";
        case "FINISHED":
            return "Finished";
        case "FAILED":
            return "Processing failed";
        default:
            return "Unknown";
    }
}

export const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
    { value: "PLAINTEXT", label: documentTypeLabel("PLAINTEXT") },
    { value: "PDF", label: documentTypeLabel("PDF") },
    { value: "WORD", label: documentTypeLabel("WORD") },
    { value: "POWERPOINT", label: documentTypeLabel("POWERPOINT") },
    { value: "MARKDOWN", label: documentTypeLabel("MARKDOWN") },
    { value: "WEBPAGE", label: documentTypeLabel("WEBPAGE") },
    { value: "AUDIO", label: documentTypeLabel("AUDIO") },
];

export type FileType = "PDF" | "WORD" | "POWERPOINT" | "MARKDOWN" | "MP3" | "WAV" | "WEBM" | "MP4" | "QUICKTIME";

export function getFileType(file: File): FileType | null {
    switch (file.type) {
        case "application/pdf":
            return "PDF";
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            return "WORD";
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            return "POWERPOINT";
        case "text/markdown":
            return "MARKDOWN";
        case "text/x-markdown":
            return "MARKDOWN";
        case "audio/mpeg":
            return "MP3";
        case "audio/wav":
            return "WAV";
        case "audio/webm":
            return "WEBM";
        case "video/webm":
            return "WEBM";
        case "audio/mp4":
            return "MP4";
        case "audio/m4a":
            return "MP4";
        case "video/mp4":
            return "MP4";
        case "video/quicktime":
            return "QUICKTIME";
        default: {
            // Fallback by extension when MIME type is missing or generic
            const name = file.name.toLowerCase();
            if (name.endsWith(".md")) return "MARKDOWN";
            if (name.endsWith(".pdf")) return "PDF";
            if (name.endsWith(".docx")) return "WORD";
            if (name.endsWith(".pptx")) return "POWERPOINT";
            if (name.endsWith(".mp3")) return "MP3";
            if (name.endsWith(".wav")) return "WAV";
            if (name.endsWith(".webm")) return "WEBM";
            if (name.endsWith(".mp4") || name.endsWith(".m4a")) return "MP4";
            if (name.endsWith(".mov")) return "QUICKTIME";
            return null;
        }
    }
}

export function getMime(docuemntType: DocumentType): string[] {
    switch (docuemntType) {
        case "PDF":
            return ["application/pdf", ".pdf"];
        case "WORD":
            return ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", ".docx"];
        case "POWERPOINT":
            return ["application/vnd.openxmlformats-officedocument.presentationml.presentation", ".pptx"];
        case "MARKDOWN":
            return ["text/markdown", "text/x-markdown", ".md"];
        case "AUDIO":
            return [
                "audio/mpeg",
                "audio/wav",
                "audio/webm",
                "audio/mp4",
                "audio/m4a",
                "video/mp4",
                "video/quicktime",
                "video/webm",
                ".mp3",
                ".wav",
                ".webm",
                ".mp4",
                ".m4a",
                ".mov",
            ];
        default:
            return [""];
    }
}

export type ItemRole = "USER" | "ASSISTANT";
export type ItemRoleNullable = ItemRole | null;

export type ItemType = "MESSAGE" | "FUNCTION_CALL" | "FUNCTION_CALL_OUTPUT" | "REASONING";

export type Language = "ENGLISH" | "SWEDISH" | "GERMAN" | "FINNISH" | "SPANISH" | "DUTCH" | "CHINESE";

export function languageLabel(langauge: Language | null): string {
    switch (langauge) {
        case "ENGLISH":
            return "English";
        case "SWEDISH":
            return "Swedish";
        case "GERMAN":
            return "German";
        case "FINNISH":
            return "Finnish";
        case "SPANISH":
            return "Spanish";
        case "DUTCH":
            return "Dutch";
        case "CHINESE":
            return "Chinese";
        default:
            return "";
    }
}

export const LANGUAGES: Language[] = ["ENGLISH", "SWEDISH", "FINNISH", "GERMAN", "SPANISH", "DUTCH", "CHINESE"];

export function useProcessing<T>(
    result: UseQueryState<T>,
    execute: UseQueryExecute,
    isProcessing: (result: UseQueryState<T>) => boolean,
) {
    useEffect(() => {
        const interval = window.setInterval(() => {
            if (!result.fetching && !result.error && result.data) {
                if (isProcessing(result)) {
                    execute({
                        requestPolicy: "cache-and-network",
                    });
                }
            }
        }, 5000);
        return () => {
            window.clearInterval(interval);
        };
    }, [result, execute, isProcessing]);
}

export type ReasoningEffortType = "MINIMAL" | "LOW" | "MEDIUM" | "HIGH";

export function reasoningEffortLabel(reasoningEffort: ReasoningEffortType | null): string {
    switch (reasoningEffort) {
        case "MINIMAL":
            return "Minimal";
        case "LOW":
            return "Low";
        case "MEDIUM":
            return "Medium";
        case "HIGH":
            return "High";
        default:
            return "";
    }
}

export const REASONING_EFFORTS: ReasoningEffortType[] = ["MINIMAL", "LOW", "MEDIUM", "HIGH"];

export type Verbosity = "LOW" | "MEDIUM" | "HIGH";

export const VERBOSITIES: Verbosity[] = ["LOW", "MEDIUM", "HIGH"];

export function verbosityLabel(v: Verbosity | null): string {
    switch (v) {
        case "LOW":
            return "Low";
        case "MEDIUM":
            return "Medium";
        case "HIGH":
            return "High";
        default:
            return "";
    }
}

// Image generation options
export const IMAGE_SIZE_OPTIONS = ["1024x1024", "1536x1024", "1024x1536"] as const;
export type ImageSizeOption = (typeof IMAGE_SIZE_OPTIONS)[number];
export const DEFAULT_IMAGE_SIZE: ImageSizeOption = "1536x1024";

export const IMAGE_QUALITY_OPTIONS = ["auto", "low", "medium", "high"] as const;
export type ImageQualityOption = (typeof IMAGE_QUALITY_OPTIONS)[number];
export const DEFAULT_IMAGE_QUALITY: ImageQualityOption = "auto";
